/* eslint-disable react-hooks/exhaustive-deps */
import { MiniLoader } from '@pwc/pwc-deals-ui';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';
import { filter } from 'lodash';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import SyncedInfo from 'common/components/Autosync/SyncedInfo.tsx';
import featureFlags, {
  isDisplayFormatEnabled,
  isIngestionFormatTypeEnabled,
} from 'common/helpers/featureFlags';
import { getAnalysisLibraryItemGroups } from '../../analysisSlice';
import UnableToLoadContent from '../UnableToLoadContent/UnableToLoadContent';
import CellRangeLibraryItem from './CellRangeLibraryItem';
import ChartLibraryItem from './ChartLibraryItem';
import PowerPointLibraryItem from './PowerPointLibraryItem';
import NamedRangeLibraryItem from './NamedRangeLibraryItem';
import styles from './ExcelPPTLibraryItemCard.module.scss';
import {
  junctionColorMapping,
  junctionSmartSymbolsMapping,
} from '../../../../common/helpers/junctionColorMapping';
import { cleanUpHTML } from '../../../../common/helpers/excelHiddenColumnsRemover';
import {
  getCachedLibraryItemsSvgHtml,
  updateLibraryItem,
} from '../../libraryItemsSlice';
import { getNormalizedLibraryData } from './utilities';
import { getUserRole } from '../../../../common/redux/security/loggedInUser/loggedInUserSlice';
import { ContentMain } from './components/ExcelPPTLibraryItemCard';
import { sanitize } from '../../../../common/services/sanitizeService';

const formatTypeMap = {
  300: 'SVG',
  301: 'SVG',
  302: 'SVG',
  500: 'SVG',
};

const extractNameFromUrl = (url) => {
  const fileUrlSplited = url.split('/');
  return fileUrlSplited[fileUrlSplited.length - 1];
};

const getLibraryItemHelper = ({ excelPPTItem, item }) => {
  switch (item.type) {
    case Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value:
      return new NamedRangeLibraryItem(
        {
          data: JSON.stringify(item),
          ...item,
        },
        excelPPTItem?.formatType
      );

    case Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value:
      return new CellRangeLibraryItem(
        {
          data: JSON.stringify(item),
          ...item,
        },
        excelPPTItem?.formatType
      );

    case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
      return new ChartLibraryItem({
        data: JSON.stringify(item),
        ...item,
      });

    case Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value:
      return new PowerPointLibraryItem({
        data: JSON.stringify(item),
        ...item,
      });

    default:
      throw new Error(`invalid type: ${item.type}`);
  }
};

const ExcelPPTLibraryItemCard = ({
  // see props from DraggableLibraryItemCard
  forView,
  hideExcelCellRangeFormatSwitch,
  innerRef,
  isKfaThumbnail,
  libraryItem,
  onFormatTypeChange, // only for PO Topic
  onComponentState = () => {},
  projectId,
  setIsDragDisabled,
  ...props
}) => {
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const dataItem = useMemo(() => {
    try {
      return JSON.parse(libraryItem);
    } catch {
      return libraryItem;
    }
  }, [libraryItem]);

  const { svgScaleFactor } = dataItem || {};

  const [excelPPTItem, setItem] = useState(libraryItem);
  const [loadingContent, setLoadingContent] = useState(false);
  const [hasError, setHasError] = useState(
    libraryItem.hasError || libraryItem.type === -1 || false
  );
  const analysisLibraryItemGroups = useSelector(getAnalysisLibraryItemGroups);
  const libraryItemsSvgHtml = useSelector(getCachedLibraryItemsSvgHtml);
  const [loadingNewFormat, setLoadingNewFormat] = useState({});
  const darkMode = useSelector((state) => state.userSettings.settings.darkMode);

  const getAnalysisSectionDetails = useCallback(() => {
    const { excelPptLibraryItemList = [] } = analysisLibraryItemGroups;
    const filterGroup = excelPptLibraryItemList.map((group) => {
      const filterItem = filter(group.excelPptLibraryItems, (item) => {
        return item.id === libraryItem.id;
      });
      const createList = () => {
        if (filterItem.length > 0) {
          return [
            {
              ...filterItem[0],
              formatType:
                (isDisplayFormatEnabled && filterItem[0]?.data?.formatType) ||
                formatTypeMap[libraryItem.type],
            },
          ];
        }
        return [];
      };

      return {
        ...group,
        libraryItems: createList(),
      };
    });

    const finalItem = filterGroup.filter((m) => m.libraryItems.length > 0);

    return finalItem.length > 0 ? finalItem[0] : {};
  }, [analysisLibraryItemGroups, libraryItem.id, libraryItem.type]);

  useEffect(() => {
    (async function usedEffect() {
      if (libraryItem.value) {
        setItem(libraryItem);
      } else if (typeof libraryItem.data === 'string') {
        try {
          setLoadingContent(true);
          await onComponentState({ type: 'LoadStart' });

          /**
           * pwc-deals-web-common
           * constants folder
           * add/modify ApiErrorCodes.js ApiErrorMapping.js in order to get back error payload
           */
          if (
            isIngestionFormatTypeEnabled &&
            forView?.type === 'freeFormAnalysisEdit' &&
            libraryItem.type ===
              Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value
          ) {
            const libraryItemHelper = new CellRangeLibraryItem({
              data: JSON.stringify(libraryItem),
              ...libraryItem,
            });
            // some very old legacy code doesn't handle Cell range corretly, set the right FormatType
            libraryItemHelper.formatType =
              libraryItemHelper.data.FormatType || libraryItemHelper.formatType;

            const payLoad = libraryItemHelper.getRequestPayload();
            const { service } = libraryItemHelper;
            const { fileName, fileContents } = await service(
              projectId,
              payLoad
            );
            const {
              autoSync,
              value: content,
              error,
            } = fileContents.find(
              ({ formatType }) => formatType === libraryItemHelper.formatType
            );
            const worksheetName =
              libraryItemHelper.data.WorksheetName ||
              autoSync?.data.worksheetName;

            Object.assign(libraryItem, {
              content,
              error,
            });

            dispatch(
              updateLibraryItem({
                id: libraryItem.id,
                item: content,
                libraryItemFormat: fileContents,
              })
            );

            setItem({
              ...libraryItem,
              sharePointItemUrl: payLoad.SharePointItemUrl,
              fileName,
              slideNumber: payLoad.SlideNumber,
              worksheetName,
              formatType: libraryItemHelper.formatType,
              libraryItemId: libraryItem.id,
              value: content,
              autoSync,
              contentsWithFormatType: undefined,
            });

            setHasError(Boolean(!content || error?.isError));
          } else {
            const libraryItemHelper = getLibraryItemHelper({
              excelPPTItem,
              item: libraryItem,
            });

            const payLoad = libraryItemHelper.getRequestPayload();
            const { service } = libraryItemHelper;
            const renderData = await service(projectId, payLoad);

            const { autoSync, value, error } =
              (renderData &&
                renderData.fileContents &&
                renderData.fileContents.length &&
                renderData.fileContents[0]) ||
              {};
            const worksheetName = autoSync
              ? autoSync.data.worksheetName
              : libraryItemHelper.extractSourceName().source;
            libraryItem.content = value;
            libraryItem.error = renderData.fileContents[0]?.error;

            const { value: secondValue } =
              (renderData &&
                renderData.fileContents &&
                renderData.fileContents.length > 1 &&
                renderData.fileContents[1]) ||
              {};

            if (isKfaThumbnail) {
              // eslint-disable-next-line no-underscore-dangle
              window.__LIBRARYITEMSTORE__.add({
                // item.fileContents?.find(el => el.formatType === 'SVG');
                fileContents: [
                  {
                    formatType: 'SVG',
                    value: secondValue,
                  },
                ],
                id: libraryItem.id,
              });
            }

            dispatch(
              updateLibraryItem({
                id: libraryItem.id,
                item: secondValue || value,
                libraryItemFormat: renderData?.fileContents,
              })
            );
            const topicContentSvg =
              excelPPTItem?.contentsWithFormatType?.[1]?.isDisplayedFormatType;

            setItem({
              ...libraryItem,
              sharePointItemUrl: payLoad.SharePointItemUrl,
              fileName: renderData.fileName,
              slideNumber: payLoad.SlideNumber,
              worksheetName,
              formatType: payLoad.FormatType || (topicContentSvg && 'SVG'),
              libraryItemId: libraryItem.id,
              value:
                payLoad.FormatType === 'SVG' || topicContentSvg
                  ? secondValue
                  : value,
              autoSync,
              contentsWithFormatType: undefined,
            });

            setHasError(!renderData || !value || (error && error.isError));
          }
        } catch (err) {
          // example err: {"apiErrorCode":"E1005-104","apiErrorMessage":"An Error Occurred","errorCode":"E1005-102-104"}
          setItem({
            ...libraryItem,
            error: {
              ...err,
              ...libraryItem.error,
            },
          });
          setHasError(true);
        } finally {
          await onComponentState({ type: 'LoadEnd' });
          setLoadingContent(false);
        }
      } else if (analysisLibraryItemGroups) {
        const sectionDetails = getAnalysisSectionDetails();
        // Do we have libraryItems? if not, we are in the shared version,
        // so stop trying to find the AutoSync params
        // added in Bug 36006 - Click on switch to the shared-with-client on an analysis rendering blank white page (1.28HotFix)
        if (sectionDetails.libraryItems) {
          const { data } = sectionDetails.libraryItems[0];
          const libraryItemHelper = getLibraryItemHelper({
            excelPPTItem,
            item: {
              data: JSON.stringify(data),
              ...sectionDetails,
            },
          });
          setItem({
            ...libraryItem,
            sharePointItemUrl: sectionDetails.sharePointItemUrl,
            worksheetName: libraryItemHelper.extractSourceName(
              sectionDetails.libraryItems[0]
            ).source,
            fileName: extractNameFromUrl(sectionDetails.sharePointItemUrl),
          });
        } else {
          setItem({
            ...libraryItem,
            sharePointItemUrl: sectionDetails.sharePointItemUrl,
            worksheetName: null,
            fileName: null,
          });
        }
      }
    })();
  }, [
    analysisLibraryItemGroups,
    getAnalysisSectionDetails,
    libraryItem,
    projectId,
  ]);

  useEffect(() => {
    if (setIsDragDisabled) {
      setIsDragDisabled(false);
    }
  }, [setIsDragDisabled]);

  const handleClickFormatType = (response, libraryItemId) => {
    if (
      libraryItemsSvgHtml &&
      Object.prototype.hasOwnProperty.call(
        libraryItemsSvgHtml,
        libraryItemId
      ) &&
      response
    ) {
      const {
        data: { formatType },
      } = response;
      setItem({
        ...excelPPTItem,
        value: libraryItemsSvgHtml[libraryItemId][formatType]?.value,
        formatType,
      });
      if (onFormatTypeChange) {
        onFormatTypeChange(
          libraryItemId,
          formatType,
          libraryItemsSvgHtml[libraryItemId]
        );
      }
    } else if (response && libraryItem) {
      const {
        data: { formatType },
      } = response;
      setItem({
        ...excelPPTItem,
        value:
          formatType === 'HTML'
            ? libraryItem.fileContents[0].value
            : libraryItem.fileContents[1].value,
        formatType,
      });
      if (onFormatTypeChange) {
        onFormatTypeChange(libraryItemId, formatType, libraryItem);
      }
    }
  };

  const mappedHtml = excelPPTItem.value
    ? cleanUpHTML(
        junctionSmartSymbolsMapping(
          junctionColorMapping(excelPPTItem.value, darkMode, libraryItem)
        )
      )
    : '';

  // prevent react invalid prop on element
  // eslint-disable-next-line no-unused-vars
  const { showAutoSync, ...passedPropsRoot } = props;
  const excelPPTItemData = getNormalizedLibraryData(excelPPTItem);

  const isDraftAndHTML =
    !libraryItem.isShared && libraryItem.formatType === 'HTML';
  const itemIsScalable = !isDraftAndHTML;

  // shared items do not send the formatType, but excel ranges can be scalled SVGs or HTMLs originally
  const getContent = () => {
    if (Constants.isSliderAvailableWhitelist.includes(libraryItem.type)) {
      // In Draft all enter here diffing as scalable or not scalable
      // For shared all of them come as scalable (as we cannot diff between hmlt/svg, if BE sends HTML without scaling this would work)
      return (
        <ContentMain
          content={mappedHtml}
          projectID={projectId}
          itemIsScalable={itemIsScalable}
          propsContentScalable={{
            valueSVGScalePercent: svgScaleFactor * 100 || 100,
          }}
          data-testid="excel-content-item"
        />
      );
    }
    return (
      <div
        data-testid="excel-content-item"
        className={`content-item-container content-item-container-scroll ${styles.contentItemContainerScroll} css-override`}
        dangerouslySetInnerHTML={{
          __html: sanitize(mappedHtml),
        }}
      />
    );
  };

  return (
    <>
      <div
        ref={innerRef}
        {...passedPropsRoot}
        className={`rich-text-library-item-edit ${
          styles.excelPptLibraryItemEdit
        } ${hasError || libraryItem.hasError ? 'unable-to-load-content' : ''}
        ${
          featureFlags.isSVGScalingEnabled
            ? styles.removeMaxWidthForSVGScaling
            : ''
        }
        `}
      >
        {!loadingContent &&
          !loadingNewFormat[libraryItem.id] &&
          // eslint-disable-next-line no-nested-ternary
          (hasError && excelPPTItem ? (
            <UnableToLoadContent
              type={
                excelPPTItem.error?.type ||
                (excelPPTItem.autoSync && excelPPTItem.autoSync.type) ||
                excelPPTItem.type
              }
              data={{
                fileName: excelPPTItem.fileName,
                sharePointItemUrl:
                  excelPPTItemData.sharePointItemUrl ||
                  excelPPTItemData.fileName,
                worksheetName:
                  excelPPTItemData.worksheetName ||
                  excelPPTItem.data?.worksheetName,
                chartName:
                  excelPPTItemData.chartName ||
                  (excelPPTItem.autoSync &&
                    excelPPTItem.autoSync.data.chartName),
                groupName: excelPPTItem.groupName,
                slideNumber: excelPPTItem.slideNumber,
                name:
                  excelPPTItem.autoSync && excelPPTItem.autoSync.data.cellRange,
              }}
              placeId="ExcelPPTLivraryItemCard"
              projectId={projectId}
              apiErrorCode={excelPPTItem.error?.apiErrorCode}
              errorCode={
                excelPPTItem.error?.errorCode ||
                excelPPTItem.error?.apiErrorCode
              }
            />
          ) : (
            getContent()
          ))}

        {(loadingContent ||
          libraryItem.isLoading ||
          loadingNewFormat[libraryItem.id]) && (
          <Box
            data-testid="mini-loader-container"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <MiniLoader />
          </Box>
        )}
        {!loadingContent &&
          !libraryItem.isShared &&
          !loadingNewFormat[libraryItem.id] &&
          !hasError &&
          excelPPTItem && (
            <SyncedInfo
              isPwcUser={isPwcUser}
              excelPPTItem={excelPPTItem}
              handleClickFormatType={handleClickFormatType}
              hideSwitcherForEditor={hideExcelCellRangeFormatSwitch}
              projectId={projectId}
              setLoading={(libraryItemId, loading) =>
                setLoadingNewFormat({
                  [libraryItemId]: loading,
                })
              }
            />
          )}
      </div>
    </>
  );
};

function itemAreEqual(prevItem, nextItem) {
  return (
    prevItem.libraryItem.id === nextItem.libraryItem.id &&
    prevItem.projectId === nextItem.projectId &&
    prevItem.libraryItem.isShared === nextItem.libraryItem.isShared &&
    prevItem.libraryItem.formatType === nextItem.libraryItem.formatType &&
    prevItem.libraryItem.data === nextItem.libraryItem.data &&
    prevItem.hideExcelCellRangeFormatSwitch ===
      nextItem.hideExcelCellRangeFormatSwitch
  );
}
export default React.memo(ExcelPPTLibraryItemCard, itemAreEqual);
